/**
 * Loonawell Cart Drawer Cross-Sell
 * ─────────────────────────────────
 * Fetches Shopify product recommendations based on cart contents
 * and injects them into the Webflow cart drawer.
 *
 * Usage: Add <script src="{{ 'cart-cross-sell.js' | asset_url }}" defer></script>
 *        at the end of your header_cpt file (after cart-drawer.js).
 *
 * Requires: A <div id="cart-cross-sell"></div> placed inside the cart drawer
 *           (see implementation notes).
 */

(function () {
  const CROSS_SELL_LIMIT = 2; // Max products to show
  const CONTAINER_ID = "cart-cross-sell";

  /* ── Manual overrides (optional) ──
     Define specific cross-sell pairings by product handle.
     If a cart item matches a key here, these products are shown
     instead of Shopify's auto-recommendations.
     
     To use: uncomment and edit the map below.
  */
  // const MANUAL_PAIRINGS = {
  //   'delicious-deer': ['sardine-dog-pate', 'happy-tummy-baked-dog-treats'],
  //   'lamb-love': ['beef-bacon-organic-dog-pate', 'superfoodies-baked-dog-treats'],
  // };

  function getContainer() {
    let container = document.getElementById(CONTAINER_ID);
    const cartList = document.querySelector('.w-commerce-commercecartlist');

    // If Udesly wiped the cart list, the container might be deleted. Recreate it.
    if (!container) {
      container = document.createElement('div');
      container.id = CONTAINER_ID;
    }

    // Always ensure it lives at the bottom of the scrollable cart list
    // so it scrolls smoothly with the items and doesn't squish the layout on mobile.
    if (cartList && container.parentNode !== cartList) {
      cartList.appendChild(container);
    }

    return container || document.getElementById(CONTAINER_ID);
  }

  async function fetchCart() {
    try {
      const res = await fetch("/cart.js", { credentials: "same-origin" });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  async function fetchRecommendations(productId) {
    try {
      const res = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=8&intent=related`
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data.products || [];
    } catch (e) {
      return [];
    }
  }

  async function addToCart(variantId) {
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ 
          items: [{ 
            id: variantId, 
            quantity: 1,
            properties: {
              "_source": "mini_cart_cross_sell"
            }
          }] 
        }),
      });
      if (!res.ok) throw new Error("Add failed");
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  function formatMoney(cents, currency) {
    const amount = (cents / 100).toFixed(2);
    const symbols = { CHF: "CHF", EUR: "€", GBP: "£", USD: "$" };
    const sym = symbols[currency] || currency + " ";

    if (currency === "CHF") return `CHF. ${amount}`;
    return `${sym}${amount}`;
  }

  function renderCrossSell(products, currency) {
    const container = getContainer();
    if (!container) return;

    if (products.length === 0) {
      container.innerHTML = "";
      container.style.display = "none";
      return;
    }

    const cards = products
      .slice(0, CROSS_SELL_LIMIT)
      .map((product) => {
        const variant = product.variants[0];
        const price = formatMoney(variant.price, currency);
        const img = product.featured_image
          ? product.featured_image.replace(/(\.\w+)(\?|$)/, "_200x$1$2")
          : "";

        return `
        <div class="cross-sell__card">
          <a href="${product.url}" class="cross-sell__img-link">
            <img src="${img}" alt="${product.title}" loading="lazy" />
          </a>
          <div class="cross-sell__info">
            <a href="${product.url}" class="cross-sell__title">${product.title}</a>
            <div class="cross-sell__price">${price}</div>
            <button
              class="cross-sell__add"
              data-variant-id="${variant.id}"
              aria-label="Add ${product.title} to cart"
            >
              ADD
            </button>
          </div>
        </div>`;
      })
      .join("");

    container.innerHTML = `
      <div class="cross-sell__header">Frequently bought together</div>
      <div class="cross-sell__grid">${cards}</div>
    `;
    container.style.display = "block";

    // Bind add buttons
    container.querySelectorAll(".cross-sell__add").forEach((btn) => {
      btn.addEventListener("click", async function () {
        const variantId = this.dataset.variantId;
        const originalText = this.textContent;

        this.textContent = "Adding...";
        this.disabled = true;

        const result = await addToCart(variantId);

        if (result) {
          this.textContent = "Added ✓";
          this.classList.add("is-added");

          // Refresh cart count badge
          const badge = document.querySelector(".cart-quantity");
          if (badge) {
            const cartData = await fetchCart();
            if (cartData) {
              badge.textContent = cartData.item_count;
              badge.style.display =
                cartData.item_count > 0 ? "block" : "none";
            }
          }

          // Trigger Udesly's native cart update cycle so it formats image URLs correctly
          if (window.Udesly && typeof window.Udesly.dispatch === "function") {
            window.Udesly.dispatch("product-added");
          } else {
            // Fallback for non-Udesly themes or if Udesly isn't loaded
            document.dispatchEvent(new CustomEvent("wf-change-cart-state"));
          }
        } else {
          this.textContent = originalText;
          this.disabled = false;
        }
      });
    });
  }

  async function loadCrossSell() {
    const container = getContainer();
    if (!container) return;

    const cart = await fetchCart();
    if (!cart || cart.items.length === 0) {
      container.innerHTML = "";
      container.style.display = "none";
      return;
    }

    const cartProductIds = cart.items.map((item) => item.product_id);
    const currency = cart.currency || "CHF";

    // Fetch recommendations for first product in cart
    // (Shopify's API takes a single product_id)
    let recommendations = [];

    for (const item of cart.items) {
      if (recommendations.length >= CROSS_SELL_LIMIT) break;

      const recs = await fetchRecommendations(item.product_id);

      // Filter out products already in cart
      const filtered = recs.filter(
        (rec) =>
          !cartProductIds.includes(rec.id) &&
          !recommendations.some((r) => r.id === rec.id) &&
          rec.available
      );

      recommendations = recommendations.concat(filtered);
    }

    renderCrossSell(recommendations.slice(0, CROSS_SELL_LIMIT), currency);
  }

  // ── Observe cart drawer opening ──
  // The cart drawer uses Webflow's commerce wrapper.
  // We watch for it becoming visible to trigger cross-sell loading.

  let crossSellDebounceTimer;
  function debounceLoadCrossSell() {
    getContainer(); // Instantly move container into scrollable list to prevent mobile scrolling bugs during the 500ms delay
    clearTimeout(crossSellDebounceTimer);
    crossSellDebounceTimer = setTimeout(() => {
      loadCrossSell();
    }, 500);
  }

  function init() {
    const wrapper = document.querySelector(
      ".w-commerce-commercecartcontainerwrapper"
    );
    if (!wrapper) return;

    const observer = new MutationObserver(() => {
      const isVisible =
        getComputedStyle(wrapper).display !== "none" &&
        getComputedStyle(wrapper).opacity !== "0";

      if (isVisible) {
        debounceLoadCrossSell();
      }
    });

    observer.observe(wrapper, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    // Also listen for cart open clicks as a backup
    // (matched by class, not data-node-type — cart-drawer.js repurposes that
    // attribute to disable Webflow's native cart handling; see neutralizeNativeWebflowCart)
    document.addEventListener("click", (e) => {
      if (e.target.closest(".cart-button")) {
        debounceLoadCrossSell();
      }
    });

    // ── Intercept Fetch API to detect cart updates ──
    if (window.fetch) {
      const originalFetch = window.fetch;
      window.fetch = async function () {
        const response = await originalFetch.apply(this, arguments);
        try {
          const url = typeof arguments[0] === "string" ? arguments[0] : (arguments[0] && arguments[0].url);
          if (url && url.match(/\/cart\/(add|update|change|clear)/)) {
            debounceLoadCrossSell();
          }
        } catch (e) {}
        return response;
      };
    }

    // ── Intercept XMLHttpRequest to detect cart updates ──
    if (window.XMLHttpRequest) {
      const origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function () {
        const url = arguments[1];
        this.addEventListener("load", function () {
          if (url && typeof url === "string" && url.match(/\/cart\/(add|update|change|clear)/)) {
            debounceLoadCrossSell();
          }
        });
        origOpen.apply(this, arguments);
      };
    }
  }

  // ── Boot ──
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
